import { type JoinableSitesDataProvider } from './cross-join/providers/joinable-sites-data-provider';
import { type SwitcherItemType } from './common/utils/links';
import { type ProviderResult } from './common/providers/as-data-provider';
import { type AppIconKey } from './ui/primitives/app-icon';
import type { ProductKeys3P } from './common/3p-product-configs';

export interface WithCloudId {
	cloudId: string;
}

export type DiscoverySection = JSX.Element;

export interface CustomLink {
	key: string;
	label: string;
	link: string;
	local: boolean;
}

export enum Permissions {
	MANAGE = 'manage',
}

export type CustomLinksResponse = CustomLink[];

export interface CurrentSite {
	products: AvailableProduct[];
	url: string;
}

export interface UserSiteDataResponse {
	currentSite: CurrentSite;
}

export interface UserPermissionResponse {
	permitted: boolean;
}

// The product keys need to match registered products in Flight Deck:
// https://bitbucket.org/atlassian/flight-deck/src/master/packages/integrations/twp-switch-to-products/contrib/
type FlightDeckProductType =
	| 'ANALYTICS'
	| 'BEACON'
	| 'BITBUCKET'
	| 'COMPASS'
	| 'CONFLUENCE'
	| 'JIRA_CUSTOMER_SERVICE'
	| 'JIRA_PRODUCT_DISCOVERY'
	| 'JIRA_SERVICE_DESK'
	| 'JIRA_SOFTWARE'
	| 'JIRA_BUSINESS'
	| 'mercury'
	| 'OPSGENIE'
	| 'ROVO'
	| 'TALENT'
	| 'STATUSPAGE'
	| 'TEAM_CENTRAL'
	| 'TRELLO'
	| 'LOOM'
	| 'DEVAI'
	| 'DX'
	| 'FEEDBACK';

// those product keys are only used internally in the app
type InternalProductType =
	| 'ATLASSIAN_HOME'
	| 'ASSETS'
	| 'COMPANY_HUB'
	| 'TEAMS'
	| 'GOALS'
	| 'PROJECTS'
	| 'SEARCH'
	| 'CHAT'
	| 'STUDIO'
	| 'ADMINISTRATION';

//TODO: Clean up/find a better way to do this
export type ThirdPartyProductType = 'THIRD_PARTY' | string;

export type CustomLinkType = 'CUSTOM_LINK';

export type OverflowButtonType = 'OVERFLOW_MORE_BUTTON' | 'OVERFLOW_LESS_BUTTON';

export type SwitcherProductType =
	| FlightDeckProductType
	| InternalProductType
	| ThirdPartyProductType
	| CustomLinkType
	| OverflowButtonType;

export type SwitcherLinkType = 'ADMINISTRATION' | string;

export enum AnalyticsItemType {
	PRODUCT = 'product',
	ADMIN = 'admin',
	TRY = 'try',
	JOIN = 'join',
	CUSTOM_LINK = 'customLink',
	EMPTY_STATE_EXPLORE_PRODUCTS = 'empty-state-explore-products',
	OVERFLOW = 'overflow',
}

export interface OrgWithUnits {
	displayName: string;
	id: string;
	units: Record<Unit['id'], Unit>;
}

export interface Organisation {
	displayName: string;
	id: string;
}

export interface Unit {
	displayName: string;
	id: string;
}

export type AvailableProduct = {
	activationDate?: string;
	adminActivationDate?: string;
	autoProvisionDate?: string;
	companyHubName?: string;
	endUserActivationDate?: string;
	firstActiveDate?: string;
	isCompanyHubPublishedAndPremium?: boolean;
	isJsmPremium?: boolean;
	productType: SwitcherProductType;
	url: string;
};

export interface AvailableSite {
	adminAccess: boolean;
	availableProducts: AvailableProduct[];
	avatar: string | null;
	cloudId?: string;
	displayName: string;
	isBerryTwist: boolean;
	org?: Organisation;
	unit?: Unit;
	url: string;
}

export interface AvailableLink {
	linkType: string;
	url: string;
}

export interface SwitcherCustomizationItem {
	id: string;
}

export interface SwitcherCustomization {
	[key: string]: SwitcherCustomizationItem[];
}

export interface AvailableProductsResponse {
	isOpenInCurrentTabEnabled?: boolean;
	isPartial: boolean;
	links: AvailableLink[];
	sites: AvailableSite[];
	startUrl?: string;
	switcherCustomization?: SwitcherCustomization;
	tryRovoChatCohort?: TryRovoChatCohort;
}

export interface GlobalApp {
	appId: string;
	environmentId: string;
	hostProduct: string;
	icon?: string;
	routePrefix: string;
	title: string;
}

export interface GlobalAppsResponse {
	globalApps: GlobalApp[];
}

export interface ThirdPartyApp {
	redirectionUrl: string;
	type: ProductKeys3P;
}
export interface ThirdPartyProductsResponse {
	thirdPartyConfig?: ThirdPartyApp[];
}

export interface JoinableSiteUser {
	avatarUrl: string;
	displayName: string;
	relevance?: number;
}

export interface JoinableSiteUserAvatarPropTypes {
	appearance: 'circle';
	name: string;
	size: 'small';
	src: string;
}

export interface JoinableProductDetails {
	capability?: 'DIRECT_ACCESS' | 'REQUEST_ACCESS';
	collaborators: JoinableSiteUser[];
	productUrl: string;
}

export interface JoinableProductsWithProductUrl {
	[key: string]: JoinableProductDetails;
}

export interface JoinableProductsWithUserIds {
	[key: string]: string[];
}

export interface JoinableSiteUsersKeyedByProduct {
	[key: string]: JoinableSiteUser[];
}

export type JoinableProducts = JoinableProductsWithProductUrl | JoinableProductsWithUserIds;

export interface JoinableSiteWithProducts {
	products: JoinableProducts;
	users?: JoinableSiteUsersKeyedByProduct;
}

export interface JoinableSiteWithUsers {
	products?: JoinableProducts;
	users: JoinableSiteUsersKeyedByProduct;
}

export type JoinableSite = {
	avatarUrl?: string;
	cloudId: string;
	displayName: string;
	relevance?: number;
	url: string;
} & (JoinableSiteWithProducts | JoinableSiteWithUsers);

export interface JoinableSitesResponse {
	sites: JoinableSite[] | undefined;
}

export enum ProductKey {
	CONFLUENCE = 'confluence.ondemand',
	JIRA_CORE = 'jira-core.ondemand',
	JIRA_SOFTWARE = 'jira-software.ondemand',
	JIRA_SERVICE_DESK = 'jira-servicedesk.ondemand',
	BITBUCKET = 'bitbucket',
	OPSGENIE = 'opsgenie',
	STATUSPAGE = 'statuspage',
	TRELLO = 'trello',
	COMPASS = 'compass',
	TEAM_CENTRAL = 'townsquare',
	JIRA_PRODUCT_DISCOVERY = 'jira-product-discovery',
}

export interface SwitcherChildItem {
	avatar: string | null;
	cloudId?: string;
	href: string;
	label: string;
}

export type AvailableProductDetails = Pick<
	SwitcherItemType,
	| 'iconKey'
	| 'key'
	| 'href'
	| 'label'
	| 'description'
	| 'iconUrl'
	| 'shouldShowNewLozenge'
	| 'autoProvisionDate'
> & {
	ordinal: number;
};

export type AvailableProductLinks = AvailableProductDetails;

export type ProductConfigurationProductIcon = {
	blue: string;
	neutral: string;
	white: string;
};

type ProductConfigurationProduct = {
	apsKey: string;
	displayName: string;
	iconKey: AppIconKey;
	icons: ProductConfigurationProductIcon;
	ordinal: number;
	siteUrlSuffix?: string;
	subtitle: i18nLabel | null;
	tcsProductKey?: string;
	tenantlessProductAri?: string;
	tenantlessUrl?: string;
};

type ProductConfigurationLink = {
	apsKey: string;
	displayName: i18nLabel;
	iconUrl: string;
	ordinal: number;
	subtitle: i18nLabel | null;
	url: string;
};

export type FlightDeckProductConfigurationArtifact = {
	flightDeckAssetsBaseUrl: string;
	links: ProductConfigurationLink[];
	products: ProductConfigurationProduct[];
};

export type ProductConfigurationMap = Record<SwitcherProductType, AvailableProductDetails>;

export type ProductConfigurationResponse = {
	links: Record<SwitcherLinkType, AvailableProductLinks>;
	products: ProductConfigurationMap;
};

export interface ProviderResults {
	availableProducts: ProviderResult<AvailableProductsResponse>;
	customLinks?: ProviderResult<CustomLinksResponse>;
	globalApps: ProviderResult<GlobalAppsResponse>;
	joinableSites: ProviderResult<JoinableSitesResponse>;
	productConfiguration: ProviderResult<ProductConfigurationResponse>;
	thirdPartyProducts: ProviderResult<ThirdPartyProductsResponse>;
}

export interface SyntheticProviderResults {
	userSiteData: ProviderResult<UserSiteDataResponse>;
}

export type SwitcherCallbackProps = {
	/**
	 * Optional callback to be executed for adding Origin Tracing info onto switcher links.
	 */
	customizeLinks?: CustomizeLinks;
};

export type SwitcherDataProviderProps = {
	/**
	 * Optional custom provider for joinable sites
	 */
	joinableSitesDataProvider?: JoinableSitesDataProvider;
};

export interface GenericShowFlag<T> {
	(arg: T): T;
}

export type SwitcherPrimitiveProps = {
	/**
	 * Optional admin URl (required for Statuspage)
	 */
	adminUrl?: string;
	/**
	 * Optional cloudID, should be provided for tenanted applications.
	 */
	cloudId?: string;
	/**
	 * A post office discovery section to be injected
	 */
	discoverySection?: DiscoverySection;
	/**
	 * Optional ability to highlight a joinable site
	 */
	highlightedJoinableItemHref?: string;
	/**
	 * Product name used for analytics events
	 */
	product: string;
	/**
	 * show flag function to consume and display product flags
	 */
	showFlag?: GenericShowFlag<any>;
};

export type AtlassianSwitcherProps = SwitcherDataProviderProps &
	SwitcherCallbackProps &
	SwitcherPrimitiveProps;

export interface AnalyticAttributes {
	[key: string]: any;
}

export interface MapUrl {
	(url: string, product: SwitcherProductType): string;
}

export interface GetExtendedAnalyticsAttributes {
	(product?: SwitcherProductType): AnalyticAttributes;
}

export interface CustomizeLinks {
	(): {
		/**
		 * Provides function to return additonal AnalyticAttributes
		 */
		getExtendedAnalyticsAttributes: GetExtendedAnalyticsAttributes;
		/**
		 * Provide function to update switcher url
		 */
		mapUrl: MapUrl;
	};
}

export interface ProductRecommendationsResponse {
	capability: ProductRecommendationsCapabilityProperty;
}

export type ProductRecommendationsCapabilityProperty = {
	DIRECT_ACCESS: Array<ProductRecommendationResource>;
	REQUEST_ACCESS: Array<ProductRecommendationResource>;
};

export type ProductRecommendationResourceWithCapability = ProductRecommendationResource & {
	capability: 'DIRECT_ACCESS' | 'REQUEST_ACCESS';
};

export type ProductRecommendationResource = {
	avatarUrl: string; //"https://site-admin-avatar-cdn.staging.public.atl-paas.net/avatars/240/lightbulb.png"
	displayName: string; //"recommendations1",
	resourceId: string; //"ari:cloud:statuspage::site/bd98310f-491e-4af3-89a1-a23c6a9db606",
	roleAri: string; //"ari:cloud:statuspage::role/product/member",
	url: string; //"https://manage.statuspagestaging.com/cloud/bd98310f-491e-4af3-89a1-a23c6a9db606",
	userAccessLevel: string; //"INTERNAL",
};

export type i18nLabel = {
	defaultMessage: string;
	description: string;
	id: string;
};

export type T3pResource = {
	datasourceId?: string;
	isUserOAuthed: boolean;
	outboundAuthUrl: string;
	providerId: string;
	type: string;
	workspaceName?: string;
	workspaceUrl?: string;
};

export type T3pAppsConfigAPIResponse = T3pResource[];

export type TryRovoChatCohort = 'not-enrolled' | 'control' | 'variant';
